import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router";
import { CreditCard, Bot, Coins, Crown, Menu } from "lucide-react";
import { useOrganizationStore } from "@/store/useOrganizationStore";
import { ProfileDropdown } from "@/components/profile/ProfileDropdown";
import { Button } from "@/components/ui/button";
import { UnlockFeaturePopup } from "@/components/common/UnlockFeaturePopup";
import { AskEmmaChat } from "./AskEmmaChat";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useCustomers } from "@/contexts/CustomerDataContext";
import FeatureUtils from "@/utility/FeatureUtils";
import { usePermissions } from "@/hooks/usePermissions";

interface HeaderProps {
  isSidebarOpen: boolean;
  onToggleSidebar: () => void;
  onToggleMobileSidebar: () => void;
}

export function Header({
  isSidebarOpen,
  onToggleSidebar,
  onToggleMobileSidebar
}: HeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { customers } = useCustomers();
  const credits = useOrganizationStore((state) => state.credits);
  const { permissions } = usePermissions();
  const [showUnlockPopup, setShowUnlockPopup] = useState(false);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleCloseUnlockPopup = useCallback(() => {
    setShowUnlockPopup(false);
  }, [setShowUnlockPopup]);

  // Reset the inactivity timer on user interaction
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Only set the timer if the sidebar is not already open
    if (!isSidebarOpen) {
      inactivityTimerRef.current = setTimeout(() => {
        setIsHighlighted(true);
      }, 30000); // 30 seconds
    }
  }, [isSidebarOpen]);

  // Set up event listeners for user activity
  useEffect(() => {
    // Initial timer
    resetInactivityTimer();

    // Add event listeners for user activity
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart"
    ];
    events.forEach((event) => {
      window.addEventListener(event, resetInactivityTimer);
    });

    // Clean up event listeners
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, resetInactivityTimer);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [resetInactivityTimer]);

  // Reset highlight when sidebar is opened
  useEffect(() => {
    if (isSidebarOpen) {
      setIsHighlighted(false);
    }
  }, [isSidebarOpen]);

  const getPageTitle = () => {
    // For customer details page
    if (location.pathname.startsWith("/customers/") && params.id) {
      const customer = customers?.find((c) => c.id === params.id);
      return customer
        ? `CUSTOMERS - ${customer.firstName} ${customer.lastName}`
        : "CUSTOMERS";
    }

    // For other pages
    const path = location.pathname.split("/").filter(Boolean)[0];
    return path ? path.toUpperCase() : "DASHBOARD";
  };

  const handleBuyCredits = () => {
    navigate("/billing");
  };

  const handleUnlockCRM = () => {
    navigate("/subscription");
  };

  return (
    <>
      <header className="h-16 border-b bg-white">
        <div className="flex h-full items-center justify-between px-3 sm:px-6">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            {/* Mobile Sidebar Toggle Button - Only visible on mobile */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleMobileSidebar}
              className="lg:hidden p-2 h-10 w-10 flex-shrink-0"
              aria-label="Toggle mobile sidebar"
            >
              <Menu className="h-5 w-5" />
            </Button>

            <h1 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 truncate">
              {getPageTitle()}
            </h1>
          </div>

          <div className="flex items-center gap-1 sm:gap-2 lg:gap-6 flex-shrink-0">
            {/* Credits section - hidden on very small screens */}
            <div className="hidden sm:flex items-center space-x-2">
              {FeatureUtils.isCRMEnabled(permissions) && (
                <>
                  <CreditCard className="h-5 w-5 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">
                    {credits} Buzz Credits
                  </span>
                </>
              )}
              {!FeatureUtils.hasFullViewAccess(permissions) && (
                <Button
                  className="flex items-center justify-center gap-2"
                  variant="default"
                  onClick={
                    FeatureUtils.isCRMEnabled(permissions)
                      ? handleBuyCredits
                      : handleUnlockCRM
                  }
                >
                  {FeatureUtils.isCRMEnabled(permissions) ? (
                    <Coins className="h-4 w-4" />
                  ) : (
                    <Crown className="h-4 w-4" />
                  )}
                  <span className="hidden sm:inline">
                    {FeatureUtils.isCRMEnabled(permissions)
                      ? "Buy Credits"
                      : "Upgrade to Premium"}
                  </span>
                </Button>
              )}
            </div>

            {/* Mobile credits button - only visible on small screens */}
            <div className="sm:hidden">
              {FeatureUtils.isCRMEnabled(permissions) &&
                !FeatureUtils.hasFullViewAccess(permissions) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBuyCredits}
                    className="p-2 h-10 w-10 min-h-[44px] min-w-[44px]"
                    aria-label="Buy Credits"
                  >
                    <Coins className="h-4 w-4" />
                  </Button>
                )}
            </div>

            <div className="flex items-center gap-1 sm:gap-2">
              <TooltipProvider>
                <Tooltip open={isHighlighted ? true : undefined}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onToggleSidebar}
                      className={cn(
                        "flex items-center gap-1 sm:gap-2 border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800 min-h-[44px]",
                        isSidebarOpen ? "bg-blue-100 text-blue-800" : "",
                        isHighlighted
                          ? "animate-pulse ring-2 ring-blue-500 ring-offset-2"
                          : ""
                      )}
                      aria-label="Open AI assistant"
                    >
                      <Bot className="h-4 w-4 sm:h-5 sm:w-5" />
                      <span className="hidden sm:inline">Ask Emma</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    className="bg-blue-50 text-blue-800 border-blue-200"
                  >
                    <p>Got a question about your business?</p>
                    <p>Ask Emma — I'm right here!</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <ProfileDropdown />
          </div>
        </div>
      </header>
      {showUnlockPopup && (
        <UnlockFeaturePopup
          isOpen={showUnlockPopup}
          onClose={handleCloseUnlockPopup}
        />
      )}
      <AskEmmaChat isOpen={isSidebarOpen} onClose={onToggleSidebar} />
    </>
  );
}
